import { API_BASE_URL } from '../config/api';
import { getPreferredMediaRecorderMimeType, isIOS } from './voiceCaptureSupport';

const getAudioConstraints = (): boolean | MediaTrackConstraints => {
  // iOS Safari can reject advanced audio constraints; keep the request simple.
  if (isIOS()) return true;
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  };
};

const requestMicrophoneStream = (): Promise<MediaStream | null> => {
  if (!navigator.mediaDevices?.getUserMedia) return Promise.resolve(null);

  return navigator.mediaDevices.getUserMedia({ audio: getAudioConstraints() })
    .catch(async (error) => {
      // Retry with the simplest constraint set if advanced options were rejected.
      if (!isIOS()) {
        try {
          return await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
          /* fall through */
        }
      }
      console.warn('Microphone access failed:', error);
      return null;
    });
};

/**
 * Start a microphone request synchronously (call directly from a tap/click handler).
 * iOS Safari requires getUserMedia to be invoked in the same user-gesture turn.
 */
export function requestMicrophoneAccess(existingStream?: MediaStream | null): Promise<MediaStream | null> {
  if (existingStream?.active) return Promise.resolve(existingStream);
  return requestMicrophoneStream();
}

export async function warmUpMicrophone(existingStream?: MediaStream | null): Promise<MediaStream | null> {
  if (existingStream?.active) return existingStream;
  return requestMicrophoneStream();
}

const TRANSCRIBE_ENDPOINTS = ['/voice/transcribe', '/voice/process'] as const;

const getAuthToken = (): string | null =>
  localStorage.getItem('token') || localStorage.getItem('auth_token');

const parseTranscriptionResponse = (data: Record<string, unknown>): string => {
  const text = typeof data.text === 'string' ? data.text.trim() : '';
  if (text) return text;

  const message = typeof data.message === 'string' ? data.message.trim() : '';
  return message;
};

async function postAudioForTranscription(
  endpoint: string,
  formData: FormData,
  token: string,
): Promise<Response> {
  return fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
}

export async function transcribeAudioBlob(blob: Blob, language?: string): Promise<string> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Please log in to use voice commands.');
  }

  const extension = blob.type.includes('mp4') || blob.type.includes('aac') ? 'm4a' : 'webm';
  let lastError = 'Transcription failed';

  for (const endpoint of TRANSCRIBE_ENDPOINTS) {
    const formData = new FormData();
    formData.append('audio', blob, `recording.${extension}`);
    if (language) formData.append('language', language);
    const businessIdStr = localStorage.getItem('businessId');
    if (businessIdStr) formData.append('business_id', businessIdStr);

    const response = await postAudioForTranscription(endpoint, formData, token);

    if (response.status === 404) {
      lastError = 'Transcription service unavailable';
      continue;
    }

    const data = await response.json().catch(() => ({} as Record<string, unknown>));

    if (!response.ok) {
      lastError = typeof data.error === 'string' ? data.error : `Transcription failed (${response.status})`;
      if (response.status === 404) continue;
      throw new Error(lastError);
    }

    const text = parseTranscriptionResponse(data);
    if (text.length > 1) return text;

    throw new Error('No speech detected in the recording');
  }

  throw new Error(lastError);
}

export function createMediaRecorder(
  stream: MediaStream,
  onData: (chunk: Blob) => void,
): MediaRecorder {
  const mimeType = getPreferredMediaRecorderMimeType();
  const recorder = mimeType
    ? new MediaRecorder(stream, { mimeType })
    : new MediaRecorder(stream);

  recorder.addEventListener('dataavailable', (event) => {
    if (event.data.size > 0) onData(event.data);
  });

  return recorder;
}

export function isTranscriptionUnavailableError(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('route not found')
    || normalized.includes('transcription service unavailable')
    || normalized.includes('not yet implemented')
  );
}
